import React, { useRef, useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import { Camera, Eye, X } from 'lucide-react';

// Ngưỡng nén: file > 1.5MB sẽ được compress xuống ≤2MB / max 1920px.
// Lý do: BE Multer limit 30MB nhưng iPhone HEIC gốc 5-15MB → 4G chậm + tốn data đại lý.
// Compress fail (Android cũ không có Web Worker, hoặc lỗi codec) → fallback file gốc.
const COMPRESS_THRESHOLD = 1.5 * 1024 * 1024;
const COMPRESS_OPTIONS = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  fileType: 'image/jpeg',
};

/**
 * ImageUploadField - File input with thumbnail preview.
 *
 * Props:
 *   label        {string}                     - Field label text
 *   value        {File|string|null}           - Current value (File object or URL string)
 *   onChange     {(file: File) => void}       - Called when user picks a new file
 *   required     {boolean}                    - Mark field as required
 *   disabled     {boolean}                    - Disable interaction
 *   onViewImage  {(url: string) => void}      - Optional callback to open full-screen modal
 */
const ImageUploadField = ({ label, value, onChange, required = false, disabled = false, onViewImage }) => {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [compressing, setCompressing] = useState(false);

  // Manage object URLs to prevent memory leaks
  useEffect(() => {
    if (!value) { setPreviewUrl(null); return; }
    if (typeof value === 'string') { setPreviewUrl(value); return; }
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [value]);

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !onChange) return;

    if (file.size > COMPRESS_THRESHOLD) {
      setCompressing(true);
      try {
        const compressed = await imageCompression(file, COMPRESS_OPTIONS);
        const finalFile = new File([compressed], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        onChange(finalFile);
      } catch (err) {
        console.warn('[image-upload-field] compress fail, fallback original file:', err?.message);
        onChange(file);
      } finally {
        setCompressing(false);
      }
      return;
    }

    onChange(file);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (inputRef.current) inputRef.current.value = '';
    if (onChange) onChange(null);
  };

  const handleViewImage = (e) => {
    e.stopPropagation();
    if (previewUrl && onViewImage) {
      onViewImage(previewUrl);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="flex items-start gap-3">
        {/* Upload trigger button */}
        <button
          type="button"
          disabled={disabled || compressing}
          onClick={() => !disabled && !compressing && inputRef.current && inputRef.current.click()}
          className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg border transition-colors
            ${disabled || compressing
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 cursor-pointer'
            }`}
        >
          <Camera size={16} />
          <span>{compressing ? 'Đang nén ảnh...' : (previewUrl ? 'Đổi ảnh' : 'Chọn ảnh')}</span>
        </button>

        {/* Hidden file input */}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          disabled={disabled}
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Thumbnail preview */}
        {previewUrl && (
          <div className="relative group w-16 h-16 flex-shrink-0">
            <img
              src={previewUrl}
              alt="Xem trước"
              className="w-16 h-16 object-cover rounded-lg border border-gray-200"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />

            {/* Overlay actions */}
            <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
              {onViewImage && (
                <button
                  type="button"
                  onClick={handleViewImage}
                  className="p-1 bg-white rounded-full text-gray-700 hover:text-brand-600"
                  title="Xem toàn màn hình"
                >
                  <Eye size={12} />
                </button>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1 bg-white rounded-full text-red-600 hover:text-red-700"
                  title="Xóa ảnh"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUploadField;
